import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { TIPO_PROJETO_GOVERNAMENTAL_LABEL, enumOptions } from '../../../../model/apm/apm-enums';
import { IGovernmentProjectAddEditRequest } from '../../../../model/apm/government-project/request/government-project-add-edit-request';
import { IGovernmentProjectResponse } from '../../../../model/apm/government-project/response/government-project-response';

interface GovernmentProjectDrawerProps {
    open: boolean;
    initial?: IGovernmentProjectResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IGovernmentProjectAddEditRequest) => void;
}

export function GovernmentProjectDrawer({ open, initial, isSaving, onClose, onSave }: GovernmentProjectDrawerProps) {
    const [form, setForm] = useState<IGovernmentProjectAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const typeError = showValidationErrors && !form.type;
    const nameError = showValidationErrors && !form.name?.trim();

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                type: initial.type,
                name: initial.name,
                responsibleName: initial.responsibleName,
                description: initial.description,
                impactIndicators: initial.impactIndicators,
                results: initial.results,
                link: initial.link,
            }
            : {});
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IGovernmentProjectAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.type || !form.name?.trim()) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            name: form.name.trim(),
            responsibleName: form.responsibleName?.trim() || undefined,
            results: form.results?.trim() || undefined,
            link: form.link?.trim() || undefined,
            description: form.description || undefined,
            impactIndicators: form.impactIndicators || undefined,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<AccountTreeIcon />}
            title={isEdit ? 'Editar projeto de governo' : 'Novo projeto de governo'}
            subtitle={isEdit ? initial?.name : 'Projeto, atividade, ação ou programa da gestão'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar projeto'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Tipo</InputLabel>
                        <Select
                            fullWidth
                            value={form.type ?? ''}
                            onChange={(e) => update({ type: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={typeError}
                        >
                            {enumOptions(TIPO_PROJETO_GOVERNAMENTAL_LABEL).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                        {typeError && <FormHelperText error>Tipo é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.name || ''}
                            onChange={(e) => update({ name: e.target.value })}
                            error={nameError}
                        />
                        {nameError && <FormHelperText error>Nome é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Responsável</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.responsibleName || ''}
                            onChange={(e) => update({ responsibleName: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Conteúdo">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Descrição</InputLabel>
                        <RichTextEditor value={form.description} onChange={(description) => update({ description })} minHeight={160} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Indicadores de impacto</InputLabel>
                        <RichTextEditor value={form.impactIndicators} onChange={(impactIndicators) => update({ impactIndicators })} minHeight={120} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Resultados</InputLabel>
                        <OutlinedInput
                            fullWidth
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: 500 }}
                            value={form.results || ''}
                            onChange={(e) => update({ results: e.target.value })}
                        />
                        <FormHelperText>{(form.results || '').length}/500 caracteres</FormHelperText>
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Link" description="Opcional: página com mais informações sobre o projeto.">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Endereço (URL)</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            placeholder="https://"
                            inputProps={{ maxLength: 500 }}
                            value={form.link || ''}
                            onChange={(e) => update({ link: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
